import { useEffect, useMemo, useRef } from "react";
import { Column, UseTableProps, UseTableReturn } from "../interfaces";
import { InventoryMovement } from "../../../../interfaces/inventoryMovements";

export const useTable = (props: UseTableProps): UseTableReturn => {
  const { columns, data, isLoading } = props;
  const hasAttemptedLoad = useRef<boolean>(false);

  useEffect(() => {
    if (isLoading) {
      hasAttemptedLoad.current = true;
    }
  }, [isLoading]);

  const calculateColumnSums = (): Record<string, number> => {
    const sums: Record<string, number> = {};

    columns.forEach((column) => {
      if (column.footer === "sum") {
        const key: string =
          column.footerFieldKey === "second"
            ? column.secondFieldKey ?? column.firstFieldKey
            : column.firstFieldKey;
        sums[key] = data.reduce((acc, row) => {
          const value: number = parseFloat(row[key]) || 0;
          return acc + value;
        }, 0);
      }
      if (column.footer === "avg") {
        const key: string =
          column.footerFieldKey === "second"
            ? column.secondFieldKey ?? column.firstFieldKey
            : column.firstFieldKey;
        sums[key] =
          data.reduce((acc, row) => {
            const value: number = parseFloat(row[key]) || 0;
            return acc + value;
          }, 0) / data.length;
      }
    });

    return sums;
  };

  const columnSums: Record<string, number> = useMemo(
    () => calculateColumnSums(),
    [data, columns],
  );

  const getFooterValue = (column: Column) => {
    const key: string =
      column.footerFieldKey === "second"
        ? column.secondFieldKey ?? column.firstFieldKey
        : column.firstFieldKey;

    if (column.footer === "sum") return columnSums[key];
    if (column.footer === "avg") return columnSums[key];

    return column.footer ?? "";
  };

  const hasRedirectToSaleColumn = useMemo((): boolean => {
    return columns.some(
      (column) => column.firstFieldType === "redirect-to-sale",
    );
  }, [columns]);

  const shouldCenterRowContent = useMemo((): boolean => {
    return columns.every(
      (column) => !column.isFirstFieldSubtitle && !column.isSecondFieldSubtitle,
    );
  }, [columns]);

  const isSaleOrCancelMovement = (row: any): boolean => {
    if (!hasRedirectToSaleColumn) return false;

    const movement = row as InventoryMovement;
    const [saleCode, saleUUID] = movement.comment
      ? movement.comment.split(" | ")
      : [];
    const isSaleOrCancelMovement: boolean =
      movement.movement_type === "sale" ||
      movement.movement_type === "sale_cancel";

    return Boolean(isSaleOrCancelMovement && saleCode && saleUUID);
  };

  return {
    columnSums,
    getFooterValue,
    hasAttemptedLoad,
    hasRedirectToSaleColumn,
    isSaleOrCancelMovement,
    shouldCenterRowContent,
  };
};
